import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  query, 
  orderBy,
  where,
  onSnapshot,
  runTransaction
} from 'firebase/firestore';
import { db } from './config';

const COLLECTION_NAME = 'bills';

// Generate next invoice number
export const generateInvoiceNumber = async () => {
  try {
    const counterRef = doc(db, 'counters', 'invoiceNumber');
    
    const result = await runTransaction(db, async (transaction) => {
      const counterDoc = await transaction.get(counterRef);
      
      let nextNumber = 1;
      if (counterDoc.exists()) {
        nextNumber = (counterDoc.data().current || 0) + 1;
      }
      
      transaction.set(counterRef, { current: nextNumber }, { merge: true });
      
      // Format: INV-01, INV-02, etc.
      const paddedNumber = String(nextNumber).padStart(2, '0');
      return `INV-${paddedNumber}`;
    });
    
    return { success: true, invoiceNumber: result };
  } catch (error) {
    console.error('Error generating invoice number:', error);
    // Fallback to timestamp-based number if transaction fails
    const timestamp = Date.now();
    const fallbackNumber = `INV-${timestamp}`;
    return { success: true, invoiceNumber: fallbackNumber };
  }
};

// Get all bills
export const getBills = async () => {
  try {
    const q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const bills = [];
    querySnapshot.forEach((doc) => {
      bills.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: bills };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Listen to bills changes in real-time
export const subscribeToBills = (callback) => {
  const q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snapshot) => {
    const bills = [];
    snapshot.forEach((doc) => {
      bills.push({ id: doc.id, ...doc.data() });
    });
    callback(bills);
  });
};

// Add new bill
export const addBill = async (billData) => {
  try {
    // Generate invoice number
    const invoiceResult = await generateInvoiceNumber();
    const invoiceNumber = invoiceResult.invoiceNumber;
    
    const docRef = await addDoc(collection(db, COLLECTION_NAME), {
      ...billData,
      invoiceNumber, // Add the formatted invoice number
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    return { success: true, id: docRef.id, invoiceNumber };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Update bill
export const updateBill = async (id, billData) => {
  try {
    const billRef = doc(db, COLLECTION_NAME, id);
    await updateDoc(billRef, {
      ...billData,
      updatedAt: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Delete bill
export const deleteBill = async (id) => {
  try {
    await deleteDoc(doc(db, COLLECTION_NAME, id));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get bills by status
export const getBillsByStatus = async (status) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME), 
      where('status', '==', status),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const bills = [];
    querySnapshot.forEach((doc) => {
      bills.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: bills };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get bills by customer
export const getBillsByCustomer = async (customerName) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME), 
      where('customer', '==', customerName),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const bills = [];
    querySnapshot.forEach((doc) => {
      bills.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: bills };
  } catch (error) {
    return { success: false, error: error.message };
  }
};


// Migrate old bills to add category from inventory items
export const migrateBillsWithCategory = async (inventoryItems) => {
  try {
    const q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    let migratedCount = 0;

    for (const billDoc of querySnapshot.docs) {
      const bill = billDoc.data();
      
      // Check if bill items have category
      const needsMigration = bill.items && bill.items.some(item => !item.category);
      
      if (needsMigration) {
        // Update items with category from inventory
        const updatedItems = bill.items.map(item => {
          if (item.category) {
            return item; // Already has category
          }
          
          // Find matching inventory item
          const invItem = inventoryItems.find(inv => 
            inv.id === item.id || inv.name.toLowerCase() === item.name.toLowerCase()
          );
          
          return {
            ...item,
            category: invItem?.category || 'Uncategorized'
          };
        });
        
        // Update bill with new items
        const billRef = doc(db, COLLECTION_NAME, billDoc.id);
        await updateDoc(billRef, {
          items: updatedItems,
          updatedAt: new Date().toISOString()
        });
        
        migratedCount++;
      }
    }
    
    return { success: true, migratedCount };
  } catch (error) {
    console.error('Error migrating bills:', error);
    return { success: false, error: error.message };
  }
};
